import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { Bell, User, Award, HelpCircle, LogOut, Settings, Shield, Mail, Gift, Star, Info, Footprints } from 'lucide-react';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import { API } from '../utils/api';
import { motion } from 'framer-motion';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import { confirmDialog } from '../components/ui/confirm-dialog';
import notify from '../utils/notify';

const COMM = `${API}/community`;

export const CommunityMenuPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useCommunityAuth();
  const isAdmin = user?.role === 'COMMUNITY_ADMIN';
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${COMM}/notifications/unread/count`, authHeaders);
        setUnreadCount(res.data.count);
      } catch {}
    };
    fetchUnread();
  }, []);

  const menuItems = [
    ...(isAdmin ? [{
      icon: <Shield size={24} />,
      title: 'Admin Dashboard',
      description: 'Manage users, posts & events',
      action: () => navigate('/community/admin'),
      testId: 'menu-admin',
      highlight: true
    }] : []),
    {
      icon: <Gift size={24} />,
      title: 'Rewards',
      description: 'Redeem your earned stars',
      action: () => navigate('/community/rewards'),
      testId: 'menu-rewards'
    },
    {
      icon: <Bell size={24} />,
      title: 'Notifications',
      description: 'View your activity updates',
      badge: unreadCount > 0 ? unreadCount : null,
      action: () => navigate('/community/notifications'),
      testId: 'menu-notifications'
    },
    {
      icon: <HelpCircle size={24} />,
      title: 'Help & Support',
      description: 'Get help with the platform',
      action: () => alert('Help & Support coming soon!'),
      testId: 'menu-help'
    },
    {
      icon: <Mail size={24} />,
      title: 'Contact Us',
      description: 'Reach out to our team',
      action: () => window.open('mailto:support@socialstar.co'),
      testId: 'menu-contact'
    }
  ];

  const handleLogout = async () => {
    if (await confirmDialog({ title: 'Logout?', message: 'You will be returned to the sign-in screen.', confirmText: 'Logout' })) {
      logout();
      navigate('/community/login');
    }
  };

  if (!user) return null;

  return (
    <div className="page-container">
      <CommunityTopHeader />
      <div className="content-wrapper">
        <div className="content-section">
          <div style={{ textAlign: 'center', marginBottom: 32, padding: 24, background: 'linear-gradient(135deg, var(--primary-teal), var(--accent-teal))', borderRadius: 20, color: 'white' }}>
            <div style={{ width: 80, height: 80, margin: '0 auto 16px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, fontWeight: 700, color: 'var(--primary-teal)' }}>
              {user.username?.charAt(0).toUpperCase() || 'C'}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{user.username}</h2>
            <p style={{ fontSize: 14, opacity: 0.9 }}>{user.email}</p>
            <p style={{ fontSize: 14, opacity: 0.9, marginTop: 8 }}>
              {isAdmin ? 'Community Admin' : 'Community Member'}
            </p>
          </div>

          <div>
            {menuItems.map((item, index) => (
              <div key={index} className="card" onClick={item.action}
                style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', marginBottom: 12,
                  ...(item.highlight && { background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)', border: '2px solid #F5A623', boxShadow: '0 4px 12px rgba(245, 166, 35, 0.2)' })
                }} data-testid={item.testId}>
                <div style={{ color: item.highlight ? '#F5A623' : 'var(--primary-teal)', flexShrink: 0 }}>{item.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: item.highlight ? '#92400E' : 'var(--text-dark)', marginBottom: 2 }}>
                    {item.title}
                    {item.highlight && <span style={{ marginLeft: 8, fontSize: 20 }}>✨</span>}
                  </div>
                  <div style={{ fontSize: 13, color: item.highlight ? '#B45309' : 'var(--text-medium)' }}>{item.description}</div>
                </div>
                {item.badge && (
                  <div style={{ background: '#DC2626', color: 'white', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{item.badge}</div>
                )}
              </div>
            ))}
          </div>

          <button className="btn-primary" style={{ background: '#DC2626', marginTop: 32 }} onClick={handleLogout} data-testid="logout-btn">
            <LogOut size={20} style={{ display: 'inline', marginRight: 8 }} />
            Logout
          </button>

          <div style={{ marginTop: 32, padding: 20, background: 'var(--background-light)', borderRadius: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--text-medium)' }}>SocialStar Community</div>
            <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>© 2026 SocialStar. All rights reserved.</div>
          </div>
        </div>
      </div>
      <CommunityBottomNav active="menu" />
    </div>
  );
};

export const CommunityNotificationsPage = () => {
  const { token } = useCommunityAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${COMM}/notifications`, authHeaders);
        setNotifications(res.data || []);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const getDotColor = (type) => {
    // Map notification type → a single dot colour. No icons, no avatars,
    // no image URLs — strictly a colored circle.
    if (type === 'new_event_nearby') return '#4DB6AC';                              // teal
    if (type === 'event_reminder_24h' || type === 'event_reminder_1h') return '#F59E0B'; // amber
    if (type?.includes('approved')) return '#10B981';                               // green
    if (type?.includes('rejected')) return '#EF4444';                               // red
    if (type?.includes('achievement') || type?.includes('reward')) return '#F59E0B';// amber
    if (type?.includes('streak') || type?.includes('habit')) return '#DC2626';      // red-600
    return '#6B7280';                                                               // neutral grey
  };

  const timeAgo = (d) => {
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="page-container">
      <CommunityTopHeader title="Notifications" showBack />
      <div style={{ padding: '12px 16px 80px', maxWidth: 520, margin: '0 auto' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#9CA3AF' }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Bell size={48} color="#D1D5DB" style={{ marginBottom: 16 }} />
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#374151', marginBottom: 8 }}>No Notifications</h3>
            <p style={{ fontSize: 14, color: '#6B7280' }}>You'll see notifications here when events happen near you or you complete challenges.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {notifications.map((n) => {
              const dotColor = getDotColor(n.type);
              return (
                <div key={n.id} data-testid={`notif-${n.id}`}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
                    background: n.read ? '#fff' : '#F8FFFE',
                    border: `1px solid ${n.read ? '#F3F4F6' : '#E0F2F1'}`,
                    borderRadius: 14, transition: 'all 0.2s',
                  }}>
                  <div
                    data-testid="notification-type-dot"
                    style={{ width: 12, height: 12, borderRadius: '50%', background: dotColor, flexShrink: 0, marginTop: 6 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#1F2937', lineHeight: 1.4, marginBottom: 4 }}>{n.message}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF' }}>{n.created_at ? timeAgo(n.created_at) : ''}</div>
                  </div>
                  {!n.read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4DB6AC', flexShrink: 0, marginTop: 6 }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <CommunityBottomNav active="menu" />
    </div>
  );
};

export const CommunityRewardsPage = () => {
  const { user, token } = useCommunityAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const userStars = user?.points || user?.social_score || 0;

  const COLORS = { orange: '#D97706', orangeLight: '#FEF3C7', teal: '#0D9488', tealLight: '#E6FFFA', surface: '#FAFAFA', border: '#E5E7EB', textPrimary: '#1F2937', textSecondary: '#6B7280', textMuted: '#9CA3AF', success: '#059669' };

  useEffect(() => {
    const fetch = async () => {
      try { const r = await axios.get(`${COMM}/rewards`, authHeaders); setRewards(r.data); } catch {} finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleRedeem = async (rewardId, cost) => {
    if (userStars < cost) { notify.error('Insufficient stars for this reward.'); return; }
    if (!(await confirmDialog({ title: 'Redeem reward?', message: `${cost} stars will be deducted.`, confirmText: 'Redeem' }))) return;
    try {
      await axios.post(`${COMM}/rewards/${rewardId}/redeem`, {}, authHeaders);
      notify.success('Reward redeemed!');
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to redeem');
    }
  };

  const getCategoryLabel = (cat) => ({ perks: 'PERKS', privileges: 'PRIVILEGES', donations: 'DONATIONS', experiences: 'EXPERIENCES' }[cat] || 'REWARD');

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page-container">
      <CommunityTopHeader />
      <div className="content-wrapper">
        <div className="content-section" style={{ padding: '0 16px' }}>
          {/* Stars Balance */}
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: '28px 24px', marginBottom: 20, border: `1px solid ${COLORS.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', overflow: 'hidden' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Your Stars Balance</div>
                <button onClick={() => setShowInfoTooltip(!showInfoTooltip)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                  <Info size={16} color={COLORS.textMuted} />
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                <span style={{ fontSize: 44, fontWeight: 700, color: COLORS.orange, lineHeight: 1, letterSpacing: '-1px' }}>{userStars.toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 14, color: COLORS.textSecondary }}>Available to redeem</div>
            </div>
            <motion.div animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
              <svg width="64" height="64" viewBox="0 0 24 24"><defs><linearGradient id="sg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FCD34D"/><stop offset="50%" stopColor="#FB923C"/><stop offset="100%" stopColor="#F97316"/></linearGradient></defs>
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="url(#sg)"/></svg>
            </motion.div>
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.teal, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Available Rewards</div>

          {rewards.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 14, color: COLORS.textMuted }}>No rewards available at this time.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {rewards.map(reward => {
                const cost = reward.cost_points || 0;
                const eligible = userStars >= cost;
                return (
                  <div key={reward.id} style={{ background: '#FFFFFF', borderRadius: 12, padding: 18, border: `1px solid ${COLORS.border}` }}>
                    <div style={{ display: 'flex', gap: 14 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Award size={20} color={COLORS.textMuted} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 3 }}>{reward.title}</h3>
                        <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{getCategoryLabel(reward.category)}</div>
                        <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.45, margin: 0 }}>{reward.description}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Star size={14} fill={COLORS.orange} color={COLORS.orange} />
                        <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.orange }}>{cost}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
                      <div style={{ fontSize: 13, color: eligible ? COLORS.success : COLORS.textMuted, fontWeight: 500 }}>
                        {eligible ? 'Eligible for redemption' : `Need ${(cost - userStars).toLocaleString()} more stars`}
                      </div>
                      {eligible ? (
                        <button onClick={() => handleRedeem(reward.id, cost)} style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', background: COLORS.teal, color: '#fff' }}>Redeem</button>
                      ) : (
                        <div style={{ padding: '10px 16px', fontSize: 13, fontWeight: 500, borderRadius: 8, background: COLORS.surface, color: COLORS.textMuted, border: `1px solid ${COLORS.border}` }}>Locked</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ height: 24 }} />
        </div>
      </div>
      <CommunityBottomNav active="menu" />
    </div>
  );
};

export default CommunityMenuPage;
